import { isNumber } from "lodash";

import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { EmployeeTitle } from "../Models/Employee/Enums/EmployeeTitle";

class EmployeeTitleHelper {
    public static isProfessional(title: EmployeeTitle): boolean {
        return this.OtherProfessionals.contains(title);
    }

    public static isNurse(title: EmployeeTitle): boolean {
        return this.getNurseTitles().contains(title);
    }

    public static getNurseTitles(): EmployeeTitle[] {
        return [
            EmployeeTitle.NursePractitioner,
            EmployeeTitle.RegisteredNurse,
            EmployeeTitle.RegisteredNurseClinician,
            EmployeeTitle.RegisteredNurseTechnician,
            EmployeeTitle.LicensedPracticalNurse,
            EmployeeTitle.NurseLegacy
        ];
    }

    public static isDoctor(title: EmployeeTitle): boolean {
        return this.getDoctorTitles().contains(title);
    }

    public static getDoctorTitles(): EmployeeTitle[] {
        return [
            EmployeeTitle.FamilyPhysician,
            EmployeeTitle.Specialist,
            EmployeeTitle.DoctorLegacy
        ];
    }

    public static canSupervise(title: EmployeeTitle): boolean {
        return this.getCanSuperviseTitles().contains(title)
            || this.isProfessional(title);
    }

    public static isStudent(title: EmployeeTitle): boolean {
        return this.getStudentTitles().contains(title);
    }

    public static getStudentTitles(): EmployeeTitle[] {
        return [
            EmployeeTitle.Student,
            EmployeeTitle.Intern,
            EmployeeTitle.Extern,
            EmployeeTitle.Resident
        ];
    }

    public static getCanSuperviseTitles(): EmployeeTitle[] {
        return [
            ...this.getDoctorTitles(),
            ...this.getNurseTitles(),
            EmployeeTitle.OtherProfession
        ];
    }

    public static sortTitleOrder(list: LicenceOrganisationRule[]): LicenceOrganisationRule[] {
        return list.sortByProperty(p => LicenceOrganisationRule.getTitleName(p))
            // Sort Doctors and Nurse at beginning
            .sortByProperty(p => EmployeeTitleHelper.isNurse(p.Title), true)
            .sortByProperty(p => EmployeeTitleHelper.isDoctor(p.Title), true)
            // Sort Student, Extern and Intern at end
            .sortByProperty(p => EmployeeTitleHelper.isStudent(p.Title), false);
    }

    public static get OtherProfessionals(): EmployeeTitle[] {
        if (!this._OtherProfessionals.any()) {
            const allTitles: EmployeeTitle[] = Object.values(EmployeeTitle).filter(p => isNumber(p));
            this._OtherProfessionals = allTitles
                .filter(p => !this.getDoctorTitles().contains(p)
                    && !this.getNurseTitles().contains(p)
                    && !this.getStudentTitles().contains(p)
                    && p !== EmployeeTitle.NONE
                    && p !== EmployeeTitle.Unknown
                    && p !== EmployeeTitle.MedicalOfficeAssistant
                );
        }
        return this._OtherProfessionals;
    }

    private static _OtherProfessionals: EmployeeTitle[] = [];
}

export { EmployeeTitleHelper };
